import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ t }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-40 w-[36rem] h-[36rem] rounded-full bg-[#C86D51]/10 blur-3xl"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            data-testid="hero-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="font-accent text-xs uppercase tracking-[0.25em] text-[#C86D51] mb-6"
          >
            {t.hero.badge}
          </motion.p>
          <h1 className="font-display tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl text-[#241C1A]">
            {t.hero.lines.map((line, i) => (
              <MaskedLine key={`${line}-${i}`} delay={0.25 + i * 0.15}>
                {i === 1 ? <em className="text-[#C86D51]">{line}</em> : line}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#6E625E]"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <motion.a
              whileTap={{ scale: 0.97 }}
              href="#negozi"
              data-testid="hero-cta-stores"
              className="inline-flex items-center gap-2 rounded-full bg-[#C86D51] text-[#FAF7F2] px-7 py-4 text-sm sm:text-base font-medium shadow-lg shadow-[#C86D51]/25 hover:bg-[#A84C32] transition-colors duration-300"
            >
              <MapPin size={18} /> {t.hero.ctaStores}
            </motion.a>
            <motion.a
              whileTap={{ scale: 0.97 }}
              href="#etica"
              data-testid="hero-cta-etica"
              className="inline-flex items-center gap-2 rounded-full border border-[#241C1A]/20 px-7 py-4 text-sm sm:text-base font-medium text-[#241C1A] hover:border-[#C86D51] hover:text-[#C86D51] transition-colors duration-300"
            >
              {t.hero.ctaEthic} <ArrowDown size={16} />
            </motion.a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: photoY }} className="relative">
            <motion.img
              src="/images/hero-cono.jpg"
              alt="Osvaldo Palermo e Antonella Gerundino, i fondatori di Fiordipanna, con due coni gelato"
              data-testid="hero-image"
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              className="w-full aspect-[3/4] object-cover rounded-t-[10rem] rounded-b-3xl shadow-2xl shadow-[#1E1614]/20"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 1.3, ease: EASE }}
              className="absolute -bottom-5 -left-5 sm:-left-8 bg-[#FAF7F2] rounded-2xl shadow-xl shadow-[#1E1614]/15 px-5 py-4 border border-[#241C1A]/10"
            >
              <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-[#6E625E]">{t.hero.cardOver}</p>
              <p className="font-display text-lg text-[#241C1A]">{t.hero.cardText}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
